/** Shared building blocks: avatar, modal, room dot, badges. */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { initials } from "../lib/format";
import type { Person } from "../lib/store";
import { IconChevron } from "./icons";

const PALETTE = ["#56632a", "#8a4f7d", "#b06a2c", "#2f6f88", "#2e7a4d", "#5b6358", "#b0392f"];
function colorFor(id: string): string {
  let h = 0;
  for (const c of id) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return PALETTE[h % PALETTE.length]!;
}

export function Avatar({ person, size = 28, showOnline = false }: { person: Pick<Person, "id" | "name" | "avatarUrl" | "kind"> & { online?: boolean }; size?: number; showOnline?: boolean }) {
  const style = { width: size, height: size, fontSize: Math.round(size * 0.38) };
  const agent = person.kind === "agent";
  return (
    <span className={`avatar${agent ? " agent" : ""}`} style={style} aria-hidden="true">
      {person.avatarUrl ? (
        <img src={person.avatarUrl} alt="" referrerPolicy="no-referrer" />
      ) : (
        <span className="avatar-letters" style={{ background: agent ? undefined : colorFor(person.id) }}>
          {initials(person.name)}
        </span>
      )}
      {showOnline && person.online && <span className="online-dot" />}
    </span>
  );
}

export function RoomDot({ color }: { color: string }) {
  return <span className="room-dot" style={{ background: color }} aria-hidden="true" />;
}

export function Badge({ count, mention }: { count: number; mention?: boolean }) {
  if (!count) return null;
  return (
    <span className={`badge${mention ? " mention" : ""}`} aria-label={`${count} unread`}>
      {mention ? `@${count}` : count > 99 ? "99+" : count}
    </span>
  );
}

export function Modal({ title, onClose, children, wide, className, modalRef }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean; className?: string; modalRef?: (el: HTMLDivElement | null) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    addEventListener("keydown", onKey);
    ref.current?.querySelector<HTMLElement>("input, textarea, select, button")?.focus();
    return () => removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className={`modal${wide ? " wide" : ""}${className ? ` ${className}` : ""}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        ref={(el) => {
          ref.current = el;
          modalRef?.(el);
        }}
      >
        <div className="modal-head">
          <h2 id="modal-title">{title}</h2>
          <button className="icon-btn" type="button" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function ErrorNote({ error }: { error: string | null }) {
  if (!error) return null;
  return (
    <div className="notice error" role="alert">
      {error}
    </div>
  );
}

export const ROLE_LABEL = { admin: "Admin", member: "Member", guest: "Guest" } as const;

/**
 * A card that folds down to its title with the arrow, for sections with many
 * choices. Open by default; the choice is remembered on this device. A link to
 * its anchor (for example Settings' links at the top) opens it again.
 */
export function Fold({ id, title, side, summary, anchor, children, className }: { id: string; title: ReactNode; side?: ReactNode; summary?: ReactNode; anchor?: string; children: ReactNode; className?: string }) {
  const key = `agora.fold.${id}`;
  const [open, setOpenState] = useState(() => {
    try {
      return localStorage.getItem(key) !== "0";
    } catch {
      return true;
    }
  });
  const setOpen = (v: boolean) => {
    setOpenState(v);
    try {
      if (v) localStorage.removeItem(key);
      else localStorage.setItem(key, "0");
    } catch {
      // storage blocked: the fold still works for this visit
    }
  };
  useEffect(() => {
    if (!anchor) return;
    const check = () => {
      if (location.hash === `#${anchor}`) setOpen(true);
    };
    check();
    addEventListener("hashchange", check);
    return () => removeEventListener("hashchange", check);
  }, [anchor]);
  return (
    <section className={`card-block fold${open ? "" : " folded"}${className ? ` ${className}` : ""}`} id={anchor}>
      <div className="block-head">
        <h2>
          <button type="button" className="fold-toggle" aria-expanded={open} onClick={() => setOpen(!open)} title={open ? "Fold" : "Open"}>
            <span className="fold-arrow" aria-hidden="true">
              <IconChevron />
            </span>
            {title}
          </button>
        </h2>
        {side && <div className="fold-side">{side}</div>}
      </div>
      {!open && summary && <p className="muted-text fold-summary">{summary}</p>}
      {open && children}
    </section>
  );
}
